import {
  CallFormInstance,
  IMeetingQuestions,
  IOaccQuestions,
  IRadioButtonAnswer,
  IWebinarQuestions,
  QuestionType,
} from './interfaces';
import { CallEventType } from '@evercall/domain/vo/call/CallEventType';
import { IDefaultSelectOption } from '@dealroadshow/uikit-select';

export const TIME_INTERVAL = 5;

export const CALL_SCHEDULE_DISCLAIMER = 'Events must be scheduled at least 6 hours in advance. A 50% fee applies if event is canceled or rescheduled within 24 hours of event start.';
export const WEBINAR_SCHEDULE_DISCLAIMER = 'Webinars must be scheduled at least 6 hours in advance. A 50% fee applies if event is canceled or rescheduled within 24 hours of event start.';

export const DEFAULT_COUNTRY_CODE = 'US';

export const DEFAULT_WEBINAR_PLATFORM_NAME = 'Zoom';

export const EVERCALL_EVENT_TYPE_OPTIONS: IDefaultSelectOption[] = [
  {
    value: CallEventType.Oacc,
    label: 'Audio Conference',
  },
  {
    value: CallEventType.Webinar,
    label: 'Video Conference (Webinar)',
  },
  {
    value: CallEventType.Meeting,
    label: 'Video Meeting (Small Group)',
  },
];

const getPseudoRadioButtonsAnswers = (
  clarificationForPositive = '',
  clarificationForNegative = '',
): Array<IRadioButtonAnswer> => (
  [
    {
      value: 'Yes',
      label: `Yes${ clarificationForPositive ? `, ${ clarificationForPositive }` : '' }`,
      dataTest: 'yes',
    },
    {
      value: 'No',
      label: `No${ clarificationForNegative ? `, ${ clarificationForNegative }` : '' }`,
      dataTest: 'no',
    },
  ]
);

export const QUESTIONS_WEBINAR: IWebinarQuestions = {
  JOIN_VIA_LINK: {
    type: QuestionType.CheckboxOnly,
    name: 'Join_Via_Link',
    explanation: 'Deal Managers must provide investors with the ' +
      'join link and a password to join the webinar seamlessly. Note that this may result in inaccurate or insufficient ' +
      'investor analytics being collected.',
    title: {
      [CallFormInstance.ScheduleYourCall]: 'Via a direct link',
      [CallFormInstance.CallSettingsForm]: 'Join via link',
    },
    dataTest: 'joinViaLink',
  },
  QA_SESSION: {
    type: QuestionType.RadioOnly,
    name: 'qaSessionWebinar',
    title: 'Will there be a Q&A session after the prepared remarks?',
    radioButtonAnswers: [
      {
        value: 'Verbal',
        label: 'Verbal',
        dataTest: 'verbal',
      },
      {
        value: 'Written',
        label: 'Written',
        dataTest: 'written',
      },
      {
        value: 'Both',
        label: 'Both Verbal & Written',
        dataTest: 'bothVerbalAndWritten',
      },
      {
        value: 'No',
        label: 'No Q&A',
        dataTest: 'noQASession',
      },
    ],
  },
  SPEAKER_PRESENTING_PROCESS: {
    type: QuestionType.RadioOnly,
    name: 'speakerPresentingProcessWebinar',
    title: 'Do the presenters wish to present live or use pre-recorded video?*',
    radioButtonAnswers: [
      {
        value: 'Speak_Live',
        label: 'Speakers will present live',
        dataTest: 'speakLive',
      },
      {
        value: 'Playback_By_Client',
        label: 'Use recorded video playback, with client to provide the fully edited media file to Evercall',
        dataTest: 'playbackByClient',
      },
      {
        value: 'Playback_By_Evercall',
        label: 'Use recorded video playback, with Evercall to record, edit and produce the media',
        dataTest: 'playbackByEvercall',
      },
    ],
  },
  SEPARATED_SESSION_LINES: {
    type: QuestionType.PseudoRadio,
    name: 'separatedSessions',
    title: 'Would you like this event to be separated into Public & Private conferences?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers(
      'I would like to have attendees placed into separate Public and Private conferences',
      'not applicable',
    ),
  },
  CALL_TRANSCRIPTION: {
    type: QuestionType.RadioOnly,
    name: 'transcriptionServicesWebinar',
    title: 'Would you like Evercall to transcribe this event?*',
    radioButtonAnswers: [
      {
        value: 'AI',
        label: 'AI Transcription (no editing)',
        dataTest: 'AI',
      },
      {
        value: 'Human',
        label: 'Human Edited Transcription',
        dataTest: 'human',
      },
      {
        value: 'No',
        label: 'No, not applicable',
        dataTest: 'no',
      },
    ],
  },
  MP4: {
    type: QuestionType.PseudoRadio,
    name: 'callRecordingProcess',
    title: 'Would you like a stand-alone MP4 of this event?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers('', 'not applicable'),
  },
  WEB_REPLAY: {
    type: QuestionType.PseudoRadio,
    name: 'webReplay',
    title: 'Would you like a Web Replay via Deal Roadshow?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers('', 'not applicable'),
  },
};

export const QUESTIONS_OACC: IOaccQuestions = {
  // Event Format
  ATTENDEE_CHECK_IN_PROCESS: {
    type: QuestionType.RadioOnly,
    name: 'attendeeCheckInProcess',
    title: {
      [CallFormInstance.ScheduleYourCall]: 'How would you like Attendees to check-in to the call?',
      [CallFormInstance.CallSettingsForm]: 'Attendee CHECK-IN PROCESS',
    },
    radioButtonAnswers: [
      {
        value: 'Registration',
        explanation: 'Enter email into event page for calendar details and streamlined event access. Includes additional security measures, improved data collection, instant entry at event start time and a backup attendant dial-in.',
        label: 'Registration (recommended)',
        dataTest: 'registration',
      },
      {
        value: 'Attendant_Check_In',
        explanation: 'Dial a phone number to be checked into the event manually by an Evercall attendant. Participants should join the queue up to 5 minutes early for processing and timely entry. Does not include an event page or expedited entry.',
        label: 'Attendant Check-In',
        dataTest: 'attendantCheckIn',
      },
    ],
  },
  QA_SESSION: {
    type: QuestionType.RadioOnly,
    name: 'qaSession',
    title: 'Will there be a Q&A session after the prepared remarks?',
    radioButtonAnswers: [
      {
        value: 'Verbal',
        label: 'Verbal',
        dataTest: 'verbal',
      },
      {
        value: 'Written',
        label: 'Written',
        dataTest: 'written',
      },
      {
        value: 'Both',
        label: 'Both Verbal & Written',
        dataTest: 'bothVerbalAndWritten',
      },
      {
        value: 'No',
        label: 'No Q&A session',
        dataTest: 'noQASession',
      },
    ],
  },
  // Advanced call options
  REGISTRATION_AHEAD: {
    type: QuestionType.PseudoRadio,
    name: 'registrationAhead',
    title: {
      [CallFormInstance.ScheduleYourCall]: 'Would you like to allow investors to register ahead of the call?',
      [CallFormInstance.CallSettingsForm]: 'Allow investors to register ahead of the call',
    },
    radioButtonAnswers: getPseudoRadioButtonsAnswers('allow investors to register ahead of the call'),
  },
  CUSTOM_SCRIPT: {
    type: QuestionType.PseudoRadio,
    name: 'customScript',
    title: {
      [CallFormInstance.ScheduleYourCall]: 'Will you provide a custom script for the Evercall Moderator to introduce the call?',
      [CallFormInstance.CallSettingsForm]: 'Client to provide custom script for call introduction',
    },
    radioButtonAnswers: getPseudoRadioButtonsAnswers(
      'a custom script will be provided',
      'use Evercall’s standard professional introduction',
    ),
  },
  ROADSHOW_SHARED_PRESENTATION: {
    type: QuestionType.PseudoRadio,
    name: 'roadshowSharedPresentation',
    title: 'Will a presentation be shared via Deal Roadshow simultaneously with this call?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers('this call will use slides hosted on Deal Roadshow', 'not applicable'),
  },
  // Add-ons
  SPEAKER_PRESENTING_PROCESS: {
    type: QuestionType.RadioOnly,
    name: 'speakerPresentingProcess',
    title: 'Do the presenters wish to present live or use pre-recorded audio?*',
    radioButtonAnswers: [
      {
        value: 'Speak_Live',
        label: 'Speakers will present live',
        dataTest: 'speakLive',
      },
      {
        value: 'Playback_By_Client',
        label: 'Use recorded audio playback, with client to provide the fully edited audio file to Evercall',
        dataTest: 'playbackByClient',
      },
      {
        value: 'Playback_By_Evercall',
        label: 'Use recorded audio playback, with Evercall to record, edit and produce the audio',
        dataTest: 'playbackByEvercall',
      },
    ],
  },
  SEPARATED_CONFERENCE_LINES: {
    type: QuestionType.PseudoRadio,
    name: 'separatedConferenceLines',
    title: 'Would you like this event to be separated into Public & Private conferences?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers(
      'I would like to have attendees placed into separate Public and Private conference lines',
      'not applicable',
    ),
  },
  SPEAKER_DASHBOARD: {
    type: QuestionType.PseudoRadio,
    name: 'speakerDashboard',
    title: 'Would you like to use our Speaker Dashboard?*',
    radioButtonAnswers: getPseudoRadioButtonsAnswers(
      'I would like to chat with the moderator, view attendees and choose who to call on for Q&A',
      'not applicable',
    ),
  },
  CALL_RECORDING: {
    type: QuestionType.PseudoRadio,
    name: 'callRecording',
    title: 'Would you like a recording of this event?',
        radioButtonAnswers: getPseudoRadioButtonsAnswers(
      'send me an MP3 recording',
      'not applicable',
    ),
  },
  RECORDED_REPLAY: {
    type: QuestionType.RadioOnly,
    name: 'recordedReplay',
    title: 'Would you like Evercall to make a replay available to your Attendees?*',
    radioButtonAnswers: [
      {
        value: 'Telephonic_Replay',
        label: 'Yes, a telephonic replay',
        dataTest: 'telephonicReplay',
      },
      {
        value: 'Web_Replay_No_Slides',
        label: 'Yes, a web replay (no slides)',
        dataTest: 'webReplayNoSlides',
      },
      {
        value: 'Web_Replay_With_Slides_Unsynced',
        label: 'Yes, a web replay (with slides) unsynced',
        dataTest: 'webReplayWithSlidesUnsynced',
      },
      {
        value: 'Web_Replay_With_Slides_Synced',
        label: 'Yes, a web replay (with slides) synced',
        dataTest: 'webReplayWithSlidesSynced',
      },
      {
        value: 'No',
        label: 'No, not applicable',
        dataTest: 'no',
      },
    ],
  },
  CALL_TRANSCRIPTION: {
    type: QuestionType.RadioOnly,
    name: 'transcriptionServices',
    title: 'Would you like Evercall to transcribe this event?*',
    radioButtonAnswers: [
      {
        value: 'AI',
        label: 'AI Transcription (no editing)',
        dataTest: 'AI',
      },
      {
        value: 'Human',
        label: 'Human Edited Transcription',
        dataTest: 'human',
      },
      {
        value: 'No',
        label: 'No, not applicable',
        dataTest: 'no',
      },
    ],
  },
};

export const QUESTIONS_MEETING: IMeetingQuestions = {
  REGISTRATION_AHEAD: {
    type: QuestionType.PseudoRadio,
    name: 'registrationAheadMeeting',
    explanation: 'Deal Managers must provide investors with the Event Page link where they can register ahead of the event with their email address to receive conference access details instantly.',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Allow investors to register ahead of the call',
    },
  },
  CUSTOMIZE_REGISTRATION: {
    type: QuestionType.PseudoRadio,
    name: 'registrationCustomization',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Customize registration',
    },
  },
  SLIDES_ONLY_ROADSHOW_REQUIRED: {
    type: QuestionType.PseudoRadio,
    name: 'slidesOnlyRoadshowRequired',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Slides-only presentation to be shared via Deal Roadshow for this call',
    },
  },
  MODERATION: {
    type: QuestionType.PseudoRadio,
    name: 'moderation',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Client to self-host this meeting (If unchecked, Evercall will provide a Moderator)',
    },
  },
  CALL_TRANSCRIPTION: {
    type: QuestionType.PseudoRadio,
    name: 'transcription',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Call Transcription (additional charges may apply)',
    },
  },
  CALL_RECORDING: {
    type: QuestionType.PseudoRadio,
    name: 'recording',
    title: {
      [CallFormInstance.CallSettingsForm]: 'Record this call (as an mp4)',
    },
  },
};
